import React, { useEffect, useState, useCallback } from 'react';
import { createApiClient } from '../lib/apiClient';
import { Loader2, Target, Mail, TrendingUp, TrendingDown, Info, Save, FileDown, Route, ChevronDown, ChevronRight, User, Clock, Truck, AlertTriangle, Globe, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = createApiClient();
const money = (n) => { const v = Number(n || 0); return `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
const BLUE = '#002FA7';
const TZ_OPTIONS = Array.from(new Set(['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu']));

const DOW = [['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'], ['thu', 'Thursday'], ['fri', 'Friday'], ['sat', 'Saturday'], ['sun', 'Sunday']];
const BUCKETS = [['fuel', 'Fuel', '#002FA7'], ['scale', 'Scale', '#0891b2'], ['repair', 'Repair', '#d97706'], ['toll', 'Toll', '#7c3aed'], ['other', 'Other', '#64748b']];

// Red below target, amber within 10% of target, green above.
const alertFor = (ppm, target) => {
  if (target == null || ppm == null) return { key: 'none', cls: 'text-slate-600', dot: 'bg-slate-300', label: '' };
  if (ppm < target) return { key: 'red', cls: 'text-red-700', dot: 'bg-red-500', label: 'Below target' };
  if (ppm < target * 1.1) return { key: 'amber', cls: 'text-amber-700', dot: 'bg-amber-500', label: 'Near target' };
  return { key: 'green', cls: 'text-green-700', dot: 'bg-green-500', label: 'On target' };
};

const rowBg = (key) => key === 'red' ? 'bg-red-50' : key === 'amber' ? 'bg-amber-50' : '';

// Horizontal stacked cost-breakdown bar for one trip.
const CostBar = ({ byType, total, scope = 'trip' }) => {
  const entries = BUCKETS.filter(([k]) => (byType?.[k] || 0) > 0);
  if (!total || entries.length === 0) return <p className="text-xs text-slate-400">No fuel, scale, repair or toll expenses recorded against this {scope}.</p>;
  return (
    <div data-testid="cost-bar">
      <div className="flex h-3 w-full rounded overflow-hidden">
        {entries.map(([k, , color]) => (
          <div key={k} title={`${k}: ${money(byType[k])}`} style={{ width: `${(byType[k] / total) * 100}%`, backgroundColor: color }} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {entries.map(([k, label, color]) => (
          <span key={k} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} />{label} · {money(byType[k])}
          </span>
        ))}
      </div>
    </div>
  );
};

const TaxProfitTab = ({ isAdmin }) => {
  const [preview, setPreview] = useState(null);
  const [trend, setTrend] = useState(null);
  const [lanes, setLanes] = useState(null);
  const [trucks, setTrucks] = useState(null);
  const [drivers, setDrivers] = useState(null);
  const [driverTrend, setDriverTrend] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const ago = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
  const [range, setRange] = useState({ start: ago, end: today });
  const [target, setTarget] = useState('');
  const [recapEmail, setRecapEmail] = useState('');
  const [dow, setDow] = useState('fri');
  const [hour, setHour] = useState(17);
  const [tz, setTz] = useState('UTC');
  const [alerts, setAlerts] = useState({ fuel: 35, repair: 15, total: 60 });
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [includeIdle, setIncludeIdle] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [savingTarget, setSavingTarget] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(true);

  const load = useCallback(async () => {
    try {
      const [p, s, t, d, dt] = await Promise.all([
        ax.get('/tax/profit-recap/preview'),
        ax.get('/tax/profit-recap/settings'),
        ax.get('/tax/profit-recap/trend?weeks=8'),
        ax.get('/tax/profit-recap/drivers'),
        ax.get('/tax/profit-recap/driver-trend?weeks=6'),
      ]);
      setPreview(p.data);
      setTrend(t.data);
      setDrivers(d.data);
      setDriverTrend(dt.data);
      setTarget(s.data.profit_per_mile_target != null ? String(s.data.profit_per_mile_target) : '');
      setRecapEmail(s.data.profit_recap_email || '');
      setDow(s.data.profit_recap_dow || 'fri');
      setHour(s.data.profit_recap_hour_utc != null ? s.data.profit_recap_hour_utc : 17);
      // Always the company profile timezone (single source of truth; admin-only to change).
      setTz(s.data.profit_recap_tz || 'America/New_York');
      setAlerts({
        fuel: Math.round((s.data.alert_fuel_pct ?? 0.35) * 100),
        repair: Math.round((s.data.alert_repair_pct ?? 0.15) * 100),
        total: Math.round((s.data.alert_total_pct ?? 0.60) * 100),
      });
    } catch (e) { setPreview({ trips: [], target: null, error: true }); }
  }, []);

  const loadLanes = useCallback(async () => {
    try {
      const [l, tk] = await Promise.all([
        ax.get(`/tax/profit-recap/lanes?start=${range.start}&end=${range.end}`),
        ax.get(`/tax/profit-recap/trucks?start=${range.start}&end=${range.end}&include_idle=${includeIdle}`),
      ]);
      setLanes(l.data);
      setTrucks(tk.data);
    } catch (e) { setLanes({ lanes: [], target: null }); setTrucks({ trucks: [], target: null }); }
  }, [range.start, range.end, includeIdle]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLanes(); }, [loadLanes]);

  const flash = (text, ok = true) => { setMsg(text); setMsgOk(ok); setTimeout(() => setMsg(''), 6000); };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await ax.get('/tax/profit-recap/pnl.pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'weekly_trip_pnl.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { flash('Could not download P&L PDF.', false); }
    finally { setDownloading(false); }
  };

  const saveTarget = async () => {
    setSavingTarget(true);
    try {
      await ax.put('/tax/profit-target', { profit_per_mile_target: Number(target || 0) });
      await Promise.all([load(), loadLanes()]);
      flash('Profit-per-mile target saved.');
    } catch (e) { flash(e.response?.data?.detail || 'Could not save target.', false); }
    finally { setSavingTarget(false); }
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      await ax.put('/tax/profit-recap/settings', { email: recapEmail });
      flash('Recap recipient saved.');
    } catch (e) { flash(e.response?.data?.detail || 'Could not save recipient.', false); }
    finally { setSavingEmail(false); }
  };

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await ax.put('/tax/profit-recap/settings', { dow, hour_utc: Number(hour), tz });
      flash('Recap schedule saved.');
    } catch (e) { flash(e.response?.data?.detail || 'Could not save schedule.', false); }
    finally { setSavingSchedule(false); }
  };

  const setLaneTarget = async (lane, value) => {
    const t = value === '' ? null : Number(value);
    try {
      await ax.put('/tax/profit-recap/lane-targets', { lane, target: t });
      await loadLanes();
    } catch (e) { flash('Could not save lane target.', false); }
  };

  const setTruckTarget = async (truck_id, value) => {
    const t = value === '' ? null : Number(value);
    try {
      await ax.put('/tax/profit-recap/truck-targets', { truck_id, target: t });
      await loadLanes();
    } catch (e) { flash('Could not save truck target.', false); }
  };

  const saveAlerts = async () => {
    setSavingAlerts(true);
    try {
      await ax.put('/tax/profit-recap/settings', {
        alert_fuel_pct: Number(alerts.fuel) / 100,
        alert_repair_pct: Number(alerts.repair) / 100,
        alert_total_pct: Number(alerts.total) / 100,
      });
      await load();
      flash('Alert thresholds saved.');
    } catch (e) { flash(e.response?.data?.detail || 'Could not save thresholds.', false); }
    finally { setSavingAlerts(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await ax.post('/tax/profit-recap/run');
      flash(r.data.sent > 0
        ? `Recap emailed to ${r.data.sent} recipient(s).`
        : (r.data.reason ? `Nothing sent — ${r.data.reason}.` : 'Prepared recap — none delivered (check email settings).'),
        r.data.sent > 0);
    } catch (e) { flash(e.response?.data?.detail || 'Could not send recap.', false); }
    finally { setSending(false); }
  };

  if (!preview) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" style={{ color: BLUE }} /></div>;

  const trips = preview.trips || [];
  const tgt = preview.target;
  const belowCount = trips.filter((t) => t.below_target).length;

  return (
    <div data-testid="tax-profit-tab" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mb-4">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        Trips are ranked by profit (load rate minus fuel, scale, repair and toll spend recorded against the load). Set a profit-per-mile target below to flag weak trips, and dispatch a Friday recap of the best and worst runs.
      </div>

      {msg && <div data-testid="profit-msg" className={`mb-4 px-4 py-2.5 rounded-md text-sm border ${msgOk ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>{msg}</div>}

      {/* Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="flex items-center gap-2 mb-1"><Target className="w-4 h-4" style={{ color: BLUE }} /><h4 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Profit-per-mile target</h4></div>
          <p className="text-xs text-slate-400 mb-3">Trips below this turn red; within 10% turn amber; above stay green.</p>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
              <input data-testid="profit-target-input" type="number" step="0.01" min="0" value={target} disabled={!isAdmin}
                onChange={(e) => setTarget(e.target.value)} placeholder="0.00"
                className="w-full border border-slate-300 rounded pl-6 pr-14 py-1.5 text-sm disabled:bg-slate-50" />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">/mi</span>
            </div>
            {isAdmin && (
              <button data-testid="profit-target-save" onClick={saveTarget} disabled={savingTarget}
                className="flex items-center gap-1 bg-[#002FA7] text-white px-3 py-1.5 rounded text-sm font-semibold hover:bg-[#00247D] disabled:opacity-40">
                {savingTarget ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="flex items-center gap-2 mb-1"><Mail className="w-4 h-4" style={{ color: BLUE }} /><h4 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Weekly profit recap</h4></div>
          <p className="text-xs text-slate-400 mb-3">Auto-sends on your chosen day to admins & dispatchers with a one-page P&L PDF attached. Add one extra recipient below.</p>
          <div className="flex items-center gap-2 mb-2">
            <input data-testid="recap-email-input" type="email" value={recapEmail} disabled={!isAdmin}
              onChange={(e) => setRecapEmail(e.target.value)} placeholder="extra@company.com (optional)"
              className="flex-1 border border-slate-300 rounded px-3 py-1.5 text-sm disabled:bg-slate-50" />
            {isAdmin && (
              <button data-testid="recap-email-save" onClick={saveEmail} disabled={savingEmail}
                className="flex items-center gap-1 bg-white border border-slate-300 text-slate-700 px-3 py-1.5 rounded text-sm font-semibold hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-40">
                {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button data-testid="recap-send-now" onClick={sendNow} disabled={sending}
                className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#00247D] disabled:opacity-40 flex-1 justify-center">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send recap now
              </button>
            )}
            <button data-testid="recap-download-pdf" onClick={downloadPdf} disabled={downloading}
              title="Download the one-page P&L that ships with the recap"
              className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded-md text-sm font-semibold hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-40">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} P&L PDF
            </button>
          </div>
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
              <span className="flex items-center gap-1 text-xs font-semibold text-slate-500"><Clock className="w-3.5 h-3.5" /> Send</span>
              <select data-testid="recap-dow" value={dow} onChange={(e) => setDow(e.target.value)}
                className="border border-slate-300 rounded px-2 py-1.5 text-sm">
                {DOW.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <select data-testid="recap-hour" value={hour} onChange={(e) => setHour(Number(e.target.value))}
                className="border border-slate-300 rounded px-2 py-1.5 text-sm">
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
              </select>
              <span className="flex items-center gap-1 text-xs text-slate-400"><Globe className="w-3.5 h-3.5" /></span>
              <select data-testid="recap-tz" value={tz} onChange={(e) => setTz(e.target.value)}
                className="border border-slate-300 rounded px-2 py-1.5 text-sm max-w-[180px]">
                {TZ_OPTIONS.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
              </select>
              <button data-testid="recap-schedule-save" onClick={saveSchedule} disabled={savingSchedule}
                className="flex items-center gap-1 bg-white border border-slate-300 text-slate-700 px-3 py-1.5 rounded text-sm font-semibold hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-40">
                {savingSchedule ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Profit-per-mile trend */}
      {trend?.points?.some((p) => p.avg_profit_per_mile != null) && (
        <div className="bg-white border border-slate-200 rounded-md p-4 mb-6" data-testid="profit-trend">
          <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4" style={{ color: BLUE }} /><h4 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Profit-per-mile trend</h4></div>
          <p className="text-xs text-slate-400 mb-3">Average profit-per-mile per week — a dip below the dashed target line means lanes are slipping.</p>
          <div style={{ width: '100%', height: 160 }}>
            <ResponsiveContainer>
              <LineChart data={trend.points} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={44} />
                <Tooltip formatter={(v) => (v != null ? `${Number(v).toFixed(3)}/mi` : '—')} labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                {trend.target != null && <ReferenceLine y={trend.target} stroke="#ef4444" strokeDasharray="4 4" />}
                <Line type="monotone" dataKey="avg_profit_per_mile" stroke={BLUE} strokeWidth={2.5} dot={{ r: 3, fill: BLUE }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Cost alert thresholds */}
      {isAdmin && (
        <div className="bg-white border border-slate-200 rounded-md p-4 mb-6" data-testid="alert-tuning">
          <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4 text-amber-600" /><h4 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Cost alert thresholds</h4></div>
          <p className="text-xs text-slate-400 mb-3">Flag a trip when a cost eats more than this share of its rate. Tune to your operation.</p>
          <div className="flex flex-wrap items-end gap-4">
            {[['fuel', 'Fuel'], ['repair', 'Repairs'], ['total', 'Total costs']].map(([k, label]) => (
              <div key={k}>
                <label className="block text-xs font-semibold text-slate-500 mb-1">{label}</label>
                <div className="relative">
                  <input data-testid={`alert-${k}-input`} type="number" min="0" max="100" value={alerts[k]}
                    onChange={(e) => setAlerts((a) => ({ ...a, [k]: e.target.value }))}
                    className="w-24 border border-slate-300 rounded pl-3 pr-7 py-1.5 text-sm" />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                </div>
              </div>
            ))}
            <button data-testid="alert-save" onClick={saveAlerts} disabled={savingAlerts}
              className="flex items-center gap-1 bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#00247D] disabled:opacity-40">
              {savingAlerts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
        </div>
      )}

      {/* Trips list */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          Trips this week <span className="text-slate-400 font-normal">· {trips.length}</span>
        </h3>
        {tgt != null && (
          <span data-testid="profit-below-count" className={`text-xs font-semibold px-2 py-1 rounded-full border ${belowCount > 0 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
            {belowCount} below target ({Number(tgt).toFixed(3)}/mi)
          </span>
        )}
      </div>

      {trips.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-sm text-slate-400">
          No delivered trips with a recorded rate in the last 7 days.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 text-left w-6"></th>
                <th className="px-3 py-2 text-left">Trip</th>
                <th className="px-3 py-2 text-left">Driver</th>
                <th className="px-3 py-2 text-left">Lane</th>
                <th className="px-3 py-2 text-right">Rate</th>
                <th className="px-3 py-2 text-right">Expenses</th>
                <th className="px-3 py-2 text-right">Miles</th>
                <th className="px-3 py-2 text-right">Profit</th>
                <th className="px-3 py-2 text-right">$/mi</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {trips.map((t) => {
                const et = t.effective_target != null ? t.effective_target : tgt;
                const a = alertFor(t.profit_per_mile, et);
                const isOpen = !!expanded[t.load_id];
                return (
                  <React.Fragment key={t.load_id}>
                  <tr data-testid={`profit-trip-${t.load_id}`} onClick={() => setExpanded((e) => ({ ...e, [t.load_id]: !e[t.load_id] }))}
                    className={`border-t border-slate-50 cursor-pointer hover:bg-slate-50/60 ${rowBg(a.key)}`}>
                    <td className="px-3 py-2 text-slate-400">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                    <td className="px-3 py-2 font-semibold text-slate-900">{t.ref}</td>
                    <td className="px-3 py-2 text-slate-600">{t.driver_name}</td>
                    <td className="px-3 py-2 text-slate-600">{t.lane || '—'}{t.effective_target != null && t.effective_target !== tgt && <span className="ml-1 text-[10px] font-bold text-[#002FA7]" title={`Lane target ${Number(t.effective_target).toFixed(3)}/mi`}>◆</span>}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{money(t.rate)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{money(t.expenses)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{t.miles ? Number(t.miles).toLocaleString() : '—'}</td>
                    <td className={`px-3 py-2 text-right font-bold ${t.profit >= 0 ? 'text-slate-900' : 'text-red-700'}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {t.profit >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                        {money(t.profit)}
                      </span>
                    </td>
                    <td className={`px-3 py-2 text-right font-semibold ${a.cls}`}>{t.profit_per_mile != null ? Number(t.profit_per_mile).toFixed(3) : '—'}</td>
                    <td className="px-3 py-2">
                      {a.label ? <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${a.cls}`}><span className={`w-2 h-2 rounded-full ${a.dot}`} />{a.label}</span> : <span className="text-slate-300 text-xs">—</span>}
                      {t.alerts?.length > 0 && <span data-testid={`trip-alert-${t.load_id}`} title={t.alerts.join(' · ')} className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5"><AlertTriangle className="w-3 h-3" />{t.alerts.length}</span>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr data-testid={`trip-breakdown-${t.load_id}`} className="bg-slate-50/40">
                      <td></td>
                      <td colSpan={9} className="px-3 py-3">
                        {t.alerts?.length > 0 && (
                          <div className="mb-2 flex flex-wrap gap-2">
                            {t.alerts.map((al, ai) => (
                              <span key={ai} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"><AlertTriangle className="w-3.5 h-3.5" />{al}</span>
                            ))}
                          </div>
                        )}
                        <p className="text-xs font-semibold text-slate-500 mb-2">Where the money went{t.expenses > 0 ? ` · ${money(t.expenses)} total` : ''}</p>
                        <CostBar byType={t.by_type} total={t.expenses} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Lane leaderboard */}
      <div className="mt-8" data-testid="lane-leaderboard">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            <Route className="w-4 h-4" style={{ color: BLUE }} /> Lane Leaderboard
          </h3>
          <div className="flex items-center gap-2 text-sm">
            <input data-testid="lane-start" type="date" value={range.start} max={range.end}
              onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              className="border border-slate-300 rounded px-2 py-1.5 text-sm" />
            <span className="text-slate-400">→</span>
            <input data-testid="lane-end" type="date" value={range.end} min={range.start}
              onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              className="border border-slate-300 rounded px-2 py-1.5 text-sm" />
          </div>
        </div>

        {!lanes ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: BLUE }} /></div>
        ) : lanes.lanes.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-sm text-slate-400">No delivered trips with a recorded rate in this range.</div>
        ) : lanes.lanes.length <= 6 ? (
          <LaneList title="All lanes · most → least profitable" testid="lanes-top" icon={<Route className="w-4 h-4 text-slate-500" />}
            rows={lanes.lanes} companyTarget={lanes.target} isAdmin={isAdmin} onSetTarget={setLaneTarget} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <LaneList title="Most profitable" testid="lanes-top" icon={<TrendingUp className="w-4 h-4 text-green-600" />}
              rows={lanes.lanes.slice(0, 5)} companyTarget={lanes.target} isAdmin={isAdmin} onSetTarget={setLaneTarget} />
            <LaneList title="Least profitable" testid="lanes-bottom" icon={<TrendingDown className="w-4 h-4 text-red-600" />}
              rows={lanes.lanes.slice(Math.max(5, lanes.lanes.length - 5)).reverse()} companyTarget={lanes.target} isAdmin={isAdmin} onSetTarget={setLaneTarget} />
          </div>
        )}
        {isAdmin && lanes?.lanes?.length > 0 && <p className="text-xs text-slate-400 mt-2">Set a per-lane $/mi target in the box on each lane to override the company target for that lane. Leave blank to use the company default.</p>}
      </div>

      {/* Truck profit leaderboard */}
      <div className="mt-8" data-testid="truck-leaderboard">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            <Truck className="w-4 h-4" style={{ color: BLUE }} /> Truck Profit
          </h3>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input data-testid="idle-toggle" type="checkbox" checked={includeIdle} onChange={(e) => setIncludeIdle(e.target.checked)} className="accent-[#002FA7]" />
            Show idle trucks
          </label>
        </div>
        <p className="text-xs text-slate-400 mb-3">Trucks auto-link to loads when dispatch assigns them. Net = trip profit − maintenance − prorated fixed costs. Click a truck for its full breakdown. Uses the date range above.</p>
        {!trucks ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: BLUE }} /></div>
        ) : trucks.trucks.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-sm text-slate-400">No delivered trips with a recorded rate in this range.</div>
        ) : (
          <EntityList testid="trucks-list" rows={trucks.trucks} idKey="truck_id" nameKey="truck_name"
            companyTarget={trucks.target} isAdmin={isAdmin} onSetTarget={setTruckTarget} targetTestidPrefix="truck-target"
            unassignedName="Unassigned truck" showMaintenance onRowClick={setDetailId} />
        )}
        {trucks?.trucks?.some((t) => t.maintenance_cost > 0 || t.fixed_cost > 0) && <p className="text-xs text-slate-400 mt-2">Net profit = trip profit minus each truck's maintenance spend and prorated fixed costs (insurance, plates, permits) in this range.</p>}
      </div>

      {/* Driver profit leaderboard */}
      <div className="mt-8" data-testid="driver-leaderboard">
        <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          <User className="w-4 h-4" style={{ color: BLUE }} /> Driver Profit · this week
        </h3>
        {!drivers ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: BLUE }} /></div>
        ) : drivers.drivers.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-sm text-slate-400">No delivered trips with a recorded rate this week.</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left w-8">#</th>
                  <th className="px-3 py-2 text-left">Driver</th>
                  <th className="px-3 py-2 text-right">Trips</th>
                  <th className="px-3 py-2 text-right">Miles</th>
                  <th className="px-3 py-2 text-right">Profit</th>
                  <th className="px-3 py-2 text-right">Avg $/mi</th>
                  <th className="px-3 py-2 text-left">6-wk trend</th>
                </tr>
              </thead>
              <tbody>
                {drivers.drivers.map((d, i) => {
                  const a = alertFor(d.avg_profit_per_mile, drivers.target);
                  const tr = driverTrend?.drivers?.find((x) => (x.driver_id || 'unassigned') === (d.driver_id || 'unassigned'));
                  const pts = (tr?.points || []).map((v, idx) => ({ x: idx, v }));
                  return (
                    <tr key={d.driver_id || d.driver_name} data-testid={`driver-row-${i}`} className={`border-t border-slate-50 ${rowBg(a.key)}`}>
                      <td className="px-3 py-2 font-bold text-slate-400">{i + 1}</td>
                      <td className="px-3 py-2 font-semibold text-slate-900">{d.driver_name}</td>
                      <td className="px-3 py-2 text-right text-slate-500">{d.trips}</td>
                      <td className="px-3 py-2 text-right text-slate-500">{d.miles ? Number(d.miles).toLocaleString() : '—'}</td>
                      <td className={`px-3 py-2 text-right font-bold ${d.total_profit >= 0 ? 'text-slate-900' : 'text-red-700'}`}>{money(d.total_profit)}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${a.cls}`}>{d.avg_profit_per_mile != null ? Number(d.avg_profit_per_mile).toFixed(3) : '—'}</td>
                      <td className="px-3 py-2" data-testid={`driver-trend-${i}`}>
                        <div className="flex items-center gap-2">
                          <div style={{ width: 90, height: 28 }}>
                            {pts.some((p) => p.v != null) ? (
                              <ResponsiveContainer>
                                <LineChart data={pts} margin={{ top: 4, right: 2, left: 2, bottom: 2 }}>
                                  <Line type="monotone" dataKey="v" stroke={BLUE} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
                                </LineChart>
                              </ResponsiveContainer>
                            ) : <span className="text-slate-300 text-xs">—</span>}
                          </div>
                          {tr?.delta != null ? (
                            <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${tr.delta > 0 ? 'text-green-700' : tr.delta < 0 ? 'text-red-700' : 'text-slate-500'}`}>
                              {tr.delta > 0 ? <ArrowUp className="w-3 h-3" /> : tr.delta < 0 ? <ArrowDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                              {Math.abs(tr.delta).toFixed(3)}
                            </span>
                          ) : <span className="text-slate-300 text-xs">—</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detailId && <TruckDetailModal truckId={detailId} range={range} isAdmin={isAdmin} onClose={() => setDetailId(null)} onSaved={loadLanes} />}
    </div>
  );
};

const TargetInput = ({ id, testid, value, onSetTarget }) => {
  const [v, setV] = useState(value != null ? String(value) : '');
  useEffect(() => { setV(value != null ? String(value) : ''); }, [value]);
  return (
    <input data-testid={testid} type="number" step="0.01" min="0" value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if ((v === '' ? null : Number(v)) !== (value ?? null)) onSetTarget(id, v); }}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
      onClick={(e) => e.stopPropagation()}
      placeholder="target" title="$/mi target (blank = company default) — press Enter or click away to save"
      className="w-20 border border-slate-300 rounded px-2 py-1 text-xs text-right" />
  );
};

const LaneList = ({ title, testid, icon, rows, companyTarget, isAdmin, onSetTarget }) => (
  <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={testid}>
    <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
      {icon}<span className="text-sm font-bold text-slate-700">{title}</span>
    </div>
    <div className="divide-y divide-slate-50">
      {rows.map((l) => {
        const et = l.effective_target != null ? l.effective_target : companyTarget;
        const a = alertFor(l.avg_profit_per_mile, et);
        const hasOverride = l.effective_target != null && l.effective_target !== companyTarget;
        return (
          <div key={l.lane} data-testid={`lane-${l.lane}`} className="flex items-center justify-between px-4 py-2.5 text-sm gap-3">
            <div className="min-w-0">
              <p className="font-medium text-slate-800 truncate">{l.lane}{hasOverride && <span className="ml-1 text-[10px] font-bold text-[#002FA7]" title="Custom lane target">◆</span>}</p>
              <p className="text-xs text-slate-400">{l.trips} trip{l.trips !== 1 ? 's' : ''}{l.avg_profit_per_mile != null && <> · <span className={a.cls}>{Number(l.avg_profit_per_mile).toFixed(3)}/mi</span></>}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {isAdmin && l.lane !== 'Unknown lane' && <TargetInput id={l.lane} testid={`lane-target-${l.lane}`} value={hasOverride ? l.effective_target : null} onSetTarget={onSetTarget} />}
              <span className={`font-bold ${l.total_profit >= 0 ? 'text-slate-900' : 'text-red-700'}`}>{money(l.total_profit)}</span>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// Generic profit leaderboard list keyed by an id (used for trucks).
const EntityList = ({ testid, rows, idKey, nameKey, companyTarget, isAdmin, onSetTarget, targetTestidPrefix, unassignedName, showMaintenance, onRowClick }) => (
  <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={testid}>
    <div className="divide-y divide-slate-50">
      {rows.map((r) => {
        const et = r.effective_target != null ? r.effective_target : companyTarget;
        const a = alertFor(r.avg_profit_per_mile, et);
        const hasOverride = r.effective_target != null && r.effective_target !== companyTarget;
        const id = r[idKey];
        const editable = isAdmin && !!id && r[nameKey] !== unassignedName;
        const bigNumber = showMaintenance ? r.net_profit : r.total_profit;
        const clickable = onRowClick && !!id;
        return (
          <div key={id || r[nameKey]} data-testid={`${testid}-row-${id || 'unassigned'}`}
            onClick={clickable ? () => onRowClick(id) : undefined}
            className={`flex items-center justify-between px-4 py-2.5 text-sm gap-3 ${clickable ? 'cursor-pointer hover:bg-slate-50' : ''}`}>
            <div className="min-w-0">
              <p className="font-medium text-slate-800 truncate">
                {r[nameKey]}
                {hasOverride && <span className="ml-1 text-[10px] font-bold text-[#002FA7]" title="Custom target">◆</span>}
                {r.idle && <span className="ml-2 text-[10px] font-bold text-slate-500 bg-slate-100 rounded-full px-1.5 py-0.5">IDLE</span>}
              </p>
              <p className="text-xs text-slate-400">
                {r.trips} trip{r.trips !== 1 ? 's' : ''}
                {r.avg_profit_per_mile != null && <> · <span className={a.cls}>{Number(r.avg_profit_per_mile).toFixed(3)}/mi</span></>}
                {showMaintenance && r.maintenance_cost > 0 && <> · <span className="text-amber-700">maint {money(r.maintenance_cost)}</span></>}
                {showMaintenance && r.fixed_cost > 0 && <> · <span className="text-purple-700">fixed {money(r.fixed_cost)}</span></>}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {editable && <TargetInput id={id} testid={`${targetTestidPrefix}-${id}`} value={hasOverride ? r.effective_target : null} onSetTarget={onSetTarget} />}
              <div className="text-right">
                <span className={`font-bold ${bigNumber >= 0 ? 'text-slate-900' : 'text-red-700'}`}>{money(bigNumber)}</span>
                {showMaintenance && (r.maintenance_cost > 0 || r.fixed_cost > 0) && <p className="text-[10px] text-slate-400">gross {money(r.total_profit)}</p>}
              </div>
              {clickable && <ChevronRight className="w-4 h-4 text-slate-300" />}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const TruckDetailModal = ({ truckId, range, isAdmin, onClose, onSaved }) => {
  const [data, setData] = useState(null);
  const [fc, setFc] = useState({ insurance: '', plates: '', permits: '' });
  const [saving, setSaving] = useState(false);

  const fetchDetail = useCallback(async () => {
    const r = await ax.get(`/tax/profit-recap/trucks/${truckId}?start=${range.start}&end=${range.end}`);
    setData(r.data);
    setFc({
      insurance: r.data.fixed_costs.insurance || '',
      plates: r.data.fixed_costs.plates || '',
      permits: r.data.fixed_costs.permits || '',
    });
  }, [truckId, range.start, range.end]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const saveFixed = async () => {
    setSaving(true);
    try {
      await ax.put('/tax/profit-recap/truck-fixed-costs', {
        truck_id: truckId,
        insurance: Number(fc.insurance || 0), plates: Number(fc.plates || 0), permits: Number(fc.permits || 0),
      });
      await fetchDetail();
      onSaved && onSaved();
    } finally { setSaving(false); }
  };

  const totalBy = data ? Object.values(data.by_type || {}).reduce((s, v) => s + v, 0) : 0;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="truck-detail-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 sticky top-0 bg-white">
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            <Truck className="w-4 h-4" style={{ color: BLUE }} /> {data?.truck_name || 'Truck'}
          </h3>
          <button data-testid="truck-detail-close" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
        </div>
        {!data ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: BLUE }} /></div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              {[['Trips', data.trip_count], ['Revenue', money(data.revenue)], ['Maint', money(data.maintenance_total)], ['Net', money(data.net_profit)]].map(([l, v], i) => (
                <div key={i} className="bg-slate-50 rounded-md py-2">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">{l}</p>
                  <p className={`text-sm font-bold ${l === 'Net' && data.net_profit < 0 ? 'text-red-700' : 'text-slate-900'}`}>{v}</p>
                </div>
              ))}
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">Cost breakdown</p>
              <CostBar byType={data.by_type} total={totalBy} scope="truck" />
            </div>

            {isAdmin && (
              <div className="border border-slate-200 rounded-md p-3">
                <p className="text-xs font-semibold text-slate-600 mb-2">Monthly fixed costs (prorated: {money(data.fixed_cost_prorated)} this range)</p>
                <div className="flex flex-wrap items-end gap-3">
                  {[['insurance', 'Insurance'], ['plates', 'Plates'], ['permits', 'Permits']].map(([k, label]) => (
                    <div key={k}>
                      <label className="block text-[11px] text-slate-500 mb-1">{label}/mo</label>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                        <input data-testid={`fixed-${k}-input`} type="number" min="0" value={fc[k]}
                          onChange={(e) => setFc((p) => ({ ...p, [k]: e.target.value }))}
                          className="w-24 border border-slate-300 rounded pl-5 pr-2 py-1.5 text-sm" />
                      </div>
                    </div>
                  ))}
                  <button data-testid="fixed-save" onClick={saveFixed} disabled={saving}
                    className="flex items-center gap-1 bg-[#002FA7] text-white px-3 py-2 rounded text-sm font-semibold hover:bg-[#00247D] disabled:opacity-40">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                  </button>
                </div>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">Trips ({data.trips.length})</p>
              {data.trips.length === 0 ? <p className="text-xs text-slate-400">No trips in this range.</p> : (
                <div className="border border-slate-100 rounded-md divide-y divide-slate-50 max-h-52 overflow-y-auto">
                  {data.trips.map((t) => (
                    <div key={t.load_id} className="flex items-center justify-between px-3 py-1.5 text-sm">
                      <span className="min-w-0 truncate text-slate-700">{t.ref}<span className="text-slate-400"> · {t.lane || '—'}</span></span>
                      <span className={`font-semibold shrink-0 ${t.profit >= 0 ? 'text-slate-800' : 'text-red-700'}`}>{money(t.profit)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">Maintenance history ({data.maintenance_logs.length})</p>
              {data.maintenance_logs.length === 0 ? <p className="text-xs text-slate-400">No maintenance recorded in this range.</p> : (
                <div className="border border-slate-100 rounded-md divide-y divide-slate-50 max-h-40 overflow-y-auto">
                  {data.maintenance_logs.map((m) => (
                    <div key={m.id} className="flex items-center justify-between px-3 py-1.5 text-sm">
                      <span className="min-w-0 truncate text-slate-600">{m.performed_at} · {m.description}</span>
                      <span className="font-semibold text-amber-700 shrink-0">{money(m.cost)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxProfitTab;
